import { useCallback, useEffect, useMemo, useState } from "react";
import { listRows, updateRow } from "@/lib/adminApi";
import { PAYMENT_STATUS, commerceMeta, commerceOptions, type Payment } from "@/lib/adminCommerce";
import {
  DataTable,
  DetailList,
  Drawer,
  EmptyState,
  ErrorBanner,
  Field,
  FilterBar,
  LoadingState,
  PageHeader,
  SearchInput,
  Select,
  StatCards,
  StatusPill,
  btnAccent,
  btnGhost,
  fieldInput,
  type Column,
} from "@/pages/admin/components/AdminUI";
import { exportToPdf } from "@/pages/admin/exportUtils";
import { formatDate, formatRupees } from "@/pages/admin/format";
import { useAdminToast } from "@/hooks/useAdminToast";

export default function AdminPayments() {
  const [rows, setRows] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const { notify, node } = useAdminToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setRows(await listRows<Payment>("payments", "paid_at"));
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "We couldn't load payments.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const update = async (id: number, patch: Partial<Payment>, message?: string) => {
    setRows((list) => list.map((row) => (row.id === id ? { ...row, ...patch } : row)));
    try {
      await updateRow("payments", id, patch);
      if (message) notify(message);
    } catch {
      setError("We couldn't save that change. Please try again.");
      load();
    }
  };

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (statusFilter !== "all" && row.status !== statusFilter) return false;
      if (!query) return true;
      return [row.transaction_ref, row.booking_ref || "", row.visitor_name || "", row.farm_name || ""]
        .join(" ")
        .toLowerCase()
        .includes(query);
    });
  }, [rows, search, statusFilter]);

  const selected = rows.find((row) => row.id === selectedId) || null;

  const totals = useMemo(() => {
    const collected = rows.filter((r) => r.status === "paid").reduce((sum, r) => sum + Number(r.amount || 0), 0);
    const pending = rows.filter((r) => r.status === "pending").reduce((sum, r) => sum + Number(r.amount || 0), 0);
    const failed = rows.filter((r) => r.status === "failed").reduce((sum, r) => sum + Number(r.amount || 0), 0);
    const refunded = rows.filter((r) => r.status === "refunded").reduce((sum, r) => sum + Number(r.amount || 0), 0);
    return { collected, pending, failed, refunded, net: collected - refunded };
  }, [rows]);

  const receiptPdf = (row: Payment) => {
    exportToPdf({
      title: `Payment Receipt — ${row.transaction_ref}`,
      subtitle: `${row.visitor_name || ""} · ${row.farm_name || ""}`,
      columns: [
        { header: "Field", value: (item) => item.label },
        { header: "Value", value: (item) => item.value },
      ],
      rows: [
        { label: "Transaction ID", value: row.transaction_ref },
        { label: "Booking reference", value: row.booking_ref || "—" },
        { label: "Visitor", value: row.visitor_name || "—" },
        { label: "Farm", value: row.farm_name || "—" },
        { label: "Amount", value: formatRupees(row.amount) },
        { label: "Gateway", value: row.gateway || "—" },
        { label: "Method", value: row.method || "—" },
        { label: "Status", value: commerceMeta(PAYMENT_STATUS, row.status).label },
        { label: "Date", value: formatDate(row.paid_at) },
      ],
    });
  };

  const columns: Column<Payment>[] = [
    {
      key: "ref",
      header: "Transaction",
      primary: true,
      render: (row) => (
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-full bg-primary-100 text-primary-700 shrink-0 flex items-center justify-center">
            <i className="ri-bank-card-line"></i>
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{row.transaction_ref}</p>
            <p className="text-xs text-foreground-500 mt-0.5">{row.booking_ref || "—"}</p>
          </div>
        </div>
      ),
    },
    { key: "visitor", header: "Visitor", render: (row) => <span className="text-sm text-foreground-700 whitespace-nowrap">{row.visitor_name || "—"}</span> },
    { key: "farm", header: "Farm", render: (row) => <span className="text-sm text-foreground-700">{row.farm_name || "—"}</span> },
    {
      key: "amount",
      header: "Amount",
      render: (row) => <span className="text-sm font-semibold text-foreground-900 whitespace-nowrap">{formatRupees(row.amount)}</span>,
    },
    {
      key: "gateway",
      header: "Gateway",
      render: (row) => <span className="text-sm text-foreground-700 whitespace-nowrap">{row.gateway || "—"}</span>,
    },
    {
      key: "method",
      header: "Method",
      render: (row) => (
        <span className="text-xs font-semibold text-secondary-700 bg-secondary-100 border border-secondary-200 px-2.5 py-1 rounded-full whitespace-nowrap">
          {row.method || "—"}
        </span>
      ),
    },
    { key: "status", header: "Status", render: (row) => <StatusPill {...commerceMeta(PAYMENT_STATUS, row.status)} /> },
    {
      key: "date",
      header: "Date",
      render: (row) => <span className="text-xs text-foreground-500 whitespace-nowrap">{formatDate(row.paid_at)}</span>,
    },
  ];

  return (
    <div>
      <PageHeader
        eyebrow="Commerce"
        title="Payments"
        subtitle="Every transaction across gateways and methods — collected, pending, failed and refunded, with downloadable receipts."
      />

      <StatCards
        stats={[
          { label: "Total collected", value: formatRupees(totals.collected), icon: "ri-money-rupee-circle-line", tone: "primary" },
          { label: "Pending", value: formatRupees(totals.pending), icon: "ri-time-line", tone: "accent" },
          { label: "Refunded", value: formatRupees(totals.refunded), icon: "ri-refund-2-line", tone: "secondary" },
          { label: "Net revenue", value: formatRupees(totals.net), icon: "ri-line-chart-line", tone: "primary" },
        ]}
      />

      {error ? (
        <div className="mt-5">
          <ErrorBanner message={error} onRetry={load} onDismiss={() => setError("")} />
        </div>
      ) : null}

      <div className="mt-5 space-y-5">
        <FilterBar>
          <SearchInput value={search} onChange={setSearch} placeholder="Search by transaction, booking, visitor…" />
          <Select
            label="Status"
            value={statusFilter}
            onChange={setStatusFilter}
            options={[{ value: "all", label: "All statuses" }, ...commerceOptions(PAYMENT_STATUS)]}
          />
          <span className="text-sm text-foreground-500 whitespace-nowrap lg:ml-auto">{filtered.length} transactions</span>
        </FilterBar>

        {loading ? (
          <LoadingState label="Loading payments…" />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon="ri-bank-card-line"
            title={rows.length === 0 ? "No payments yet" : "No payments match"}
            message="Transactions will appear here as bookings are paid."
          />
        ) : (
          <DataTable columns={columns} rows={filtered} onRowClick={(row) => setSelectedId(row.id)} />
        )}
      </div>

      <Drawer
        open={Boolean(selected)}
        onClose={() => setSelectedId(null)}
        title={selected?.transaction_ref || "Transaction"}
        subtitle="Payment detail"
        footer={
          selected ? (
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => receiptPdf(selected)} className={btnGhost}>
                <i className="ri-file-download-line"></i> Receipt
              </button>
              {selected.status === "paid" ? (
                <button
                  type="button"
                  onClick={() => update(selected.id, { status: "refunded" }, "Payment marked as refunded.")}
                  className={btnAccent}
                >
                  <i className="ri-refund-2-line"></i> Mark refunded
                </button>
              ) : null}
            </div>
          ) : null
        }
      >
        {selected ? (
          <>
            <StatusPill {...commerceMeta(PAYMENT_STATUS, selected.status)} />

            <DetailList
              items={[
                { icon: "ri-calendar-check-line", label: "Booking reference", value: selected.booking_ref || "—" },
                { icon: "ri-user-3-line", label: "Visitor", value: selected.visitor_name || "—" },
                { icon: "ri-plant-line", label: "Farm", value: selected.farm_name || "—" },
                { icon: "ri-money-rupee-circle-line", label: "Amount", value: formatRupees(selected.amount) },
                { icon: "ri-secure-payment-line", label: "Gateway", value: selected.gateway || "—" },
                { icon: "ri-wallet-3-line", label: "Method", value: selected.method || "—" },
                { icon: "ri-calendar-line", label: "Paid on", value: formatDate(selected.paid_at) },
              ]}
            />

            <Field label="Transaction status">
              <select
                value={selected.status}
                onChange={(event) => update(selected.id, { status: event.target.value }, "Payment status updated.")}
                className={fieldInput}
              >
                {commerceOptions(PAYMENT_STATUS).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>
          </>
        ) : null}
      </Drawer>

      <p className="text-xs text-foreground-500 mt-5">
        <i className="ri-information-line"></i> Download a receipt for any transaction.
      </p>
      {node}
    </div>
  );
}